import { useParams } from 'react-router-dom';
import { Link } from '../components/nav';
import { PlaceMap } from '../features/map/PlaceMap';
import { SourceBadge } from '../components/SourceBadge';
import { STATUS_LABEL, type Itinerary, type ItineraryStop } from '../data/types';
import { useAppData } from '../state/AppData';
import { useSelection } from '../state/selection';
import { chapterRoman } from '../features/reader/segments';


export function ItineraryList() {
  const { itineraries } = useAppData();
  return (
    <div className="page">
      <h1>Itinerarios</h1>
      <p>
        Tres recorridos por la Segovia que describe Quadrado. Cada parada enlaza con el pasaje del libro que la
        describe y con su lugar en el mapa, para poder leer y mirar a la vez.
      </p>
      <p className="note">
        Las duraciones y las distancias son orientativas y ninguno de los recorridos se ha comprobado sobre el
        terreno: antes de caminarlos con un grupo hay que revisar accesos, aceras y seguridad.
      </p>
      {itineraries.length === 0 && <p>Todavía no hay itinerarios publicados.</p>}
      <ul className="itin-list">
        {itineraries.map((it) => (
          <li key={it.id}>
            <h2><Link to={`/itinerarios/${it.slug}`}>{it.number}. {it.title}</Link></h2>
            {it.subtitle && <p className="itin-list__sub">{it.subtitle}</p>}
            <p>{it.summary}</p>
            <p className="itin-meta">
              {it.stopCount} paradas · {it.durationMinutes} min aprox. · nivel {it.level}
              {it.locatedStopCount < it.stopCount && <> · {it.stopCount - it.locatedStopCount} sin ubicación en el mapa</>}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ItineraryView() {
  const { slug } = useParams();
  const { itineraries, placeById } = useAppData();
  const it = itineraries.find((x) => x.slug === slug);
  if (!it) {
    return (
      <div className="page">
        <h1>Itinerario no encontrado</h1>
        <p><Link to="/itinerarios">Ver los itinerarios publicados</Link></p>
      </div>
    );
  }
  const places = it.stops.map((s) => placeById.get(s.placeId)).filter((p): p is NonNullable<typeof p> => Boolean(p));
  return (
    <div className="layout layout--itinerary">
      <div className="pane pane--itin">
        <article className="itin">
          <p className="itin__back"><Link to="/itinerarios">← Todos los itinerarios</Link></p>
          <h1>{it.title}</h1>
          {it.subtitle && <p className="itin__sub">{it.subtitle}</p>}
          <p>{it.summary}</p>
          <ul className="chips" aria-label="Datos del itinerario">
            <li>{it.stopCount} paradas</li>
            <li>{it.durationMinutes} min aprox.</li>
            <li>Nivel {it.level}</li>
            <li>{(it.straightLineMeters / 1000).toFixed(1)} km en línea recta</li>
          </ul>
          <h2>Objetivos</h2>
          <ul>{it.objectives.map((o) => <li key={o}>{o}</li>)}</ul>
          <p className="note"><strong>Accesibilidad y seguridad.</strong> {it.accessibility} {it.distanceNote}</p>
          <h2>Paradas</h2>
          <ol className="stops">
            {it.stops.map((s) => <Stop key={s.order} stop={s} />)}
          </ol>
          <h2>Para cerrar</h2>
          <p>{it.closing}</p>
          <p className="itin__meta">
            <SourceBadge kind="editorial" detail={`revisión ${it.lastReviewed ?? '—'}`} />
          </p>
        </article>
      </div>
      <div className="pane pane--map"><PlaceMap places={places} route /></div>
    </div>
  );
}

function Stop({ stop }: { stop: ItineraryStop }) {
  const sel = useSelection();
  const { mentionById } = useAppData();
  const mention = mentionById.get(stop.mentionId);
  const selected = sel.place === stop.placeId;
  return (
    <li className={selected ? 'stop is-current' : 'stop'} aria-current={selected ? 'true' : undefined} data-stop={stop.order}>
      <h3>
        {stop.placeName}
        <span className="stop__status"> · {STATUS_LABEL[stop.currentStatus] ?? stop.currentStatus}</span>
        {!stop.located && <span className="stop__status"> · sin ubicación en el mapa</span>}
      </h3>
      <blockquote className="stop__quote">
        <p>«{stop.quote}»</p>
        <footer>
          <SourceBadge kind="quadrado" detail={`p. ${stop.printedPage ?? '—'} · cap. ${chapterRoman(stop.paragraphId)}`} />
        </footer>
      </blockquote>
      <p className="stop__note"><SourceBadge kind="editorial" /> {stop.editorialNote}</p>
      <p className="stop__question"><strong>Para mirar:</strong> {stop.question}</p>
      <p className="stop__actions">
        {mention && <button type="button" className="link-button" onClick={() => sel.readInContext(mention)}>Leer el pasaje completo</button>}
        {stop.located && <> · <button type="button" className="link-button" onClick={() => sel.selectPlace(stop.placeId, 'list')}>Ver en el mapa</button></>}
        {' · '}<Link to={`/lugares/${stop.placeSlug}`}>Ficha del lugar</Link>
      </p>
    </li>
  );
}

export type { Itinerary };
